import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Check, LucideAngularModule, Star } from 'lucide-angular';
import { CustomerHomeJourney, CustomerHomeJourneyStep } from '../shared/customer-home.model';

/**
 * TVG-170: the client-facing journey timeline.
 *
 * Five steps — Quote, Viewing, Provisional, Confirmed, Planning — whose lit
 * position is driven server-side by the item's real lifecycle stage. Completed
 * steps (and the confirmed Planning step) read green with a tick; the current
 * step reads in the brand colour as "you are here"; upcoming steps are muted.
 *
 * Each step is selectable and shows a short plain explanation; the current step
 * carries the relevant next action (e.g. "Book a viewing"), emitted to the
 * parent via `action` so the host decides what the action does.
 */
@Component({
  selector: 'app-customer-journey-timeline',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './customer-journey-timeline.component.html',
})
export class CustomerJourneyTimelineComponent {
  protected readonly CheckIcon = Check;
  protected readonly StarIcon = Star;

  private _journey: CustomerHomeJourney | null = null;
  protected selectedStep = 0;

  @Input({ required: true })
  set journey(value: CustomerHomeJourney | null) {
    this._journey = value;
    // Default the open step to "you are here" so the relevant action shows first.
    this.selectedStep = value?.current_step ?? 0;
  }
  get journey(): CustomerHomeJourney | null {
    return this._journey;
  }

  /** Records the host's pending state for the viewing action so the CTA can reflect it. */
  @Input() viewingRequested = false;

  /** Emits the selected step's `key` when its action is pressed. */
  @Output() action = new EventEmitter<string>();

  protected get steps(): CustomerHomeJourneyStep[] {
    return this._journey?.steps ?? [];
  }

  /**
   * Columns follow the number of steps so the timeline always lays out on a
   * single row that fits its container — the client timeline has five steps,
   * the internal Contact timeline (TVG-174) has seven. `repeat(5, …)` is exactly
   * what the previous fixed `grid-cols-5` produced, so the client view is
   * unchanged; the seven-step internal view no longer wraps or overflows.
   */
  protected get gridTemplateColumns(): string {
    return `repeat(${this.steps.length || 1}, minmax(0, 1fr))`;
  }

  protected get active(): CustomerHomeJourneyStep | null {
    return this.steps[this.selectedStep] ?? null;
  }

  protected select(index: number): void {
    this.selectedStep = index;
  }

  protected onAction(step: CustomerHomeJourneyStep): void {
    this.action.emit(step.key);
  }
}
